import { useState, DragEvent } from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Star, Briefcase, GraduationCap, Sparkles, FolderPlus, GripVertical } from "lucide-react";
import { SectionVisibility } from "@/components/SectionVisibilityControls";
import { cn } from "@/lib/utils";

export type SectionKey = "personal" | "skills" | "experience" | "education" | "summary" | "custom";

interface SectionConfig {
  key: SectionKey;
  label: string;
  icon: React.ElementType;
  visibilityKey?: keyof SectionVisibility;
}

const SECTION_CONFIGS: SectionConfig[] = [
  { key: "personal", label: "Personal", icon: User },
  { key: "skills", label: "Skills", icon: Star, visibilityKey: "skills" },
  { key: "experience", label: "Experience", icon: Briefcase, visibilityKey: "experience" },
  { key: "education", label: "Education", icon: GraduationCap, visibilityKey: "education" },
  { key: "summary", label: "Summary", icon: Sparkles, visibilityKey: "summary" },
  { key: "custom", label: "Custom", icon: FolderPlus, visibilityKey: "customSections" },
];

export const DEFAULT_SECTION_ORDER: SectionKey[] = ["personal", "summary", "skills", "experience", "education", "custom"];

interface DraggableSectionTabsProps {
  sectionOrder: SectionKey[];
  onOrderChange: (newOrder: SectionKey[]) => void;
  sectionVisibility: SectionVisibility;
}

export const DraggableSectionTabs = ({
  sectionOrder,
  onOrderChange,
  sectionVisibility,
}: DraggableSectionTabsProps) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: DragEvent<HTMLButtonElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...sectionOrder];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    
    onOrderChange(newOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Build ordered configs based on current order
  const orderedConfigs = sectionOrder.map((key) => 
    SECTION_CONFIGS.find((config) => config.key === key)!
  ).filter(Boolean);

  return (
    <TabsList className="grid w-full grid-cols-6 gap-0.5">
      {orderedConfigs.map((config, index) => {
        const Icon = config.icon;
        const isDisabled = config.visibilityKey ? !sectionVisibility[config.visibilityKey] : false;
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;

        return (
          <TabsTrigger
            key={config.key}
            value={config.key}
            disabled={isDisabled}
            draggable={!isDisabled}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={cn(
              "gap-1 text-xs cursor-grab active:cursor-grabbing transition-all relative group",
              isDragging && "opacity-50 scale-95",
              isDragOver && "ring-2 ring-primary ring-offset-1"
            )}
          >
            <GripVertical className="w-3 h-3 opacity-0 group-hover:opacity-50 absolute left-0.5 hidden sm:block" />
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{config.label}</span>
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
};

export { SECTION_CONFIGS };
